import React from 'react'
import { Chip, ChipProps } from '@mui/material'
import {
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Cancel as CancelIcon,
  Schedule as ScheduleIcon,
  Block as BlockIcon,
} from '@mui/icons-material'

export type StatusType = 
  | 'active' 
  | 'inactive' 
  | 'draft' 
  | 'archived' 
  | 'pending' 
  | 'approved' 
  | 'rejected' 
  | 'completed' 
  | 'cancelled'
  | 'in_progress'
  | 'success'
  | 'error'
  | 'warning'
  | 'info'

interface StatusChipProps extends Omit<ChipProps, 'color'> {
  status: StatusType | string
  customColors?: Record<string, {
    color: ChipProps['color']
    icon?: React.ReactElement
  }>
}

const StatusChip: React.FC<StatusChipProps> = ({ 
  status, 
  customColors = {},
  ...props 
}) => {
  const getStatusConfig = (status: string) => {
    // Check custom colors first
    if (customColors[status]) {
      return customColors[status]
    }

    // Default status configurations
    const statusConfigs: Record<string, { color: ChipProps['color'], icon?: React.ReactElement }> = {
      active: { color: 'success', icon: <CheckCircleIcon /> },
      inactive: { color: 'default', icon: <BlockIcon /> },
      draft: { color: 'warning', icon: <ScheduleIcon /> },
      archived: { color: 'default', icon: <CancelIcon /> },
      pending: { color: 'warning', icon: <ScheduleIcon /> },
      approved: { color: 'success', icon: <CheckCircleIcon /> },
      rejected: { color: 'error', icon: <CancelIcon /> },
      completed: { color: 'success', icon: <CheckCircleIcon /> },
      cancelled: { color: 'error', icon: <CancelIcon /> },
      in_progress: { color: 'info', icon: <ScheduleIcon /> },
      success: { color: 'success', icon: <CheckCircleIcon /> },
      error: { color: 'error', icon: <CancelIcon /> },
      warning: { color: 'warning', icon: <WarningIcon /> },
      info: { color: 'info', icon: <ScheduleIcon /> },
    }

    return statusConfigs[status.toLowerCase()] || { color: 'default' as ChipProps['color'] }
  }

  const config = getStatusConfig(status)
  
  // Format label for display
  const formatLabel = (status: string) => {
    return status
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  return (
    <Chip
      label={formatLabel(status)}
      color={config.color}
      icon={config.icon}
      size="small"
      variant="outlined"
      sx={{
        borderRadius: 4,
        fontWeight: 500,
        '& .MuiChip-icon': {
          fontSize: '0.875rem',
        },
        ...props.sx,
      }}
      {...props}
    />
  )
}

export default StatusChip